import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getSettings } from '../../models/settingsModel';
import { API_BASE_URL } from '../../config'; // Import API_BASE_URL
import api from '../../services/api';

// Sidebar theme color definitions
const SIDEBAR_THEMES = {
  default: 'linear-gradient(to bottom, #0099e0, #007acc)',
  ocean: 'linear-gradient(to bottom, #0077b6, #023e8a)',
  forest: 'linear-gradient(to bottom, #2d6a4f, #1b4332)',
  sunset: 'linear-gradient(to bottom, #e85d04, #9d0208)',
  purple: 'linear-gradient(to bottom, #7b2cbf, #5a189a)',
  slate: 'linear-gradient(to bottom, #475569, #1e293b)',
  teal: 'linear-gradient(to bottom, #0d9488, #115e59)',
  rose: 'linear-gradient(to bottom, #e11d48, #9f1239)',
};

function Sidebar({ isOpen, toggleSidebar, isCollapsed = false, toggleSidebarCollapse }) {
  const location = useLocation();
  const { 
    user, 
    can, 
    hasRole, 
    isIT, 
    isProcurement, 
    isStaff, 
    isSupervisor, 
    isManager, 
    isHod, 
    isDirector, 
    isApprover,
    isCallCenter,
    getUserId 
  } = useAuth();
  const [openMenus, setOpenMenus] = useState({});
  const [siteLogo, setSiteLogo] = useState('');
  const [weatherData, setWeatherData] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [weatherError, setWeatherError] = useState(null);
  const [sidebarTheme, setSidebarTheme] = useState(() => {
    // Initialize from localStorage or default
    return localStorage.getItem('sidebar_theme') || 'default';
  });
  
  // Base route for all navigation - set to empty since dashboard is already at the root level
  const baseRoute = "";

  // Simple native title tooltips - no complex JavaScript needed

  // Listen for theme changes from Settings page
  useEffect(() => {
    const handleThemeChange = (event) => {
      setSidebarTheme(event.detail);
    };
    
    window.addEventListener('sidebarThemeChange', handleThemeChange);
    return () => window.removeEventListener('sidebarThemeChange', handleThemeChange);
  }, []);

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await getSettings(); 
        
        if (response.status === 1) {
          if (response.data.site_logo_preview) { 
            setSiteLogo(response.data.site_logo_preview);
          }
          // Also get sidebar theme from settings if available
          if (response.data.sidebar_theme) {
            setSidebarTheme(response.data.sidebar_theme);
            localStorage.setItem('sidebar_theme', response.data.sidebar_theme);
          }
        } else {
          console.log('No logo found in site_logo_preview');
        }
      } catch (error) {
        console.error("Failed to fetch site logo for sidebar", error);
      }
    };
    fetchLogo();
  }, []);

  // Weather fetching function
  const fetchWeather = async () => {
    try {
      setWeatherLoading(true);
      const response = await api.get('/api/weather');
      
      if (response.data.status === 1) {
        setWeatherData(response.data.data);
        setWeatherError(null);
      } else {
        setWeatherError('Unable to fetch weather data');
      }
    } catch (err) {
      console.error('Error fetching weather:', err);
      setWeatherError('Weather service unavailable');
    } finally {
      setWeatherLoading(false);
    }
  };

  useEffect(() => {
    fetchWeather();
  }, []);

  useEffect(() => {
    // Auto-open menus based on current route
    const path = location.pathname;
    let menuToOpen = null;

    if (path.includes('/dashboard/assets')) {
      menuToOpen = 'assets';
    } else if (
      path.includes('/dashboard/procurement') ||
      path.includes('/dashboard/rfqs') ||
      path.includes('/dashboard/specifications') ||
      path.includes('/dashboard/requisitions') ||
      path.includes('/dashboard/specifications/esr')
    ) {
      // Keep procurement menu open for all procurement-related routes
      menuToOpen = 'procurement';
    } else if (path.includes('/dashboard/tanker')) {
      menuToOpen = 'water';
    } else if (path.includes('/dashboard/mms') || path.includes('/mms')) {
      menuToOpen = 'mms';
    } else if ( 
      path.includes('/dashboard/helpdesk') ||
      path.includes('/dashboard/chats') ||
      path.includes('/dashboard/development')
    ) {
      menuToOpen = 'it';
    } else if (path.includes('/dashboard/reports')) {
      menuToOpen = 'reports';
    }

    setOpenMenus(menuToOpen ? { [menuToOpen]: true } : {});
  }, [location.pathname]);

  const toggleMenu = (menu) => {
    setOpenMenus(prev => {
      const isCurrentlyOpen = !!prev[menu];
      if (isCurrentlyOpen) {
        return {};
      }
      return { [menu]: true };
    });
  };

  const isActive = (path) => {
    // Strip query parameters for comparison
    const cleanPath = path.split('?')[0];
    const currentPath = location.pathname;
    
    // Special handling for specifications paths to avoid conflicts
    if (cleanPath === '/specifications') {
      return currentPath === '/dashboard/specifications' || 
             (currentPath.startsWith('/dashboard/specifications') && 
              !currentPath.includes('/dashboard/specifications/esr'));
    } else if (cleanPath === '/esr') {
      return currentPath.includes('/dashboard/specifications/esr');
    } else if (cleanPath === '/requisitions') {
      // Only match exact requisitions pages, not sub-pages like /po, /archive, /junk
      return currentPath === '/dashboard/requisitions' || 
             (currentPath.startsWith('/dashboard/requisitions') && 
              !currentPath.includes('/dashboard/requisitions/po') &&
              !currentPath.includes('/dashboard/requisitions/archive') &&
              !currentPath.includes('/dashboard/requisitions/junk'));
    } else if (cleanPath === '/admin/gl-codes') {
      // GL codes maps to /dashboard/settings/gl-codes
      return currentPath.startsWith('/dashboard/settings/gl-codes');
    } else if (cleanPath === '/admin/suppliers') {
      // Supplier admin path maps under procurement section
      return currentPath.startsWith('/dashboard/procurement/suppliers');
    }
    
    // If path is already with /dashboard prefix
    if (cleanPath.startsWith('/dashboard')) {
      return currentPath.startsWith(cleanPath);
    }
    // Otherwise, check with /dashboard prefix
    return currentPath.startsWith(`/dashboard${cleanPath}`);
  };
  
  const isExactActive = (path) => {
    // Strip query parameters for comparison
    const cleanPath = path.split('?')[0];
    const currentPath = location.pathname;
    
    // If path is already with /dashboard prefix
    if (cleanPath.startsWith('/dashboard')) {
      return currentPath === cleanPath;
    }
    // Otherwise, check with /dashboard prefix
    return currentPath === `/dashboard${cleanPath}`;
  };

  const isAdmin = () => Number(getUserId()) === 1;

  const NavLink = ({ to, exact, children, icon, ...props }) => {
    // Check if the path is already prefixed with /dashboard
    const path = to.startsWith('/dashboard') ? to : `/dashboard${to}`;
    const active = exact ? isExactActive(to) : isActive(to);
    
    // Extract text content for collapsed state
    const getTextContent = () => {
      if (typeof children === 'string') return children;
      
      try {
        // Try to extract text from children structure
        if (children?.props?.children) {
          const childArray = Array.isArray(children.props.children) ? children.props.children : [children.props.children];
          const textChild = childArray.find(child => typeof child === 'string');
          if (textChild) return textChild;
        }
        
        // Look for span with text content
        if (Array.isArray(children)) {
          const textChild = children.find(child => typeof child === 'string' || child?.props?.children);
          if (typeof textChild === 'string') return textChild;
          if (textChild?.props?.children) return textChild.props.children;
        }
        
        return 'Menu Item';
      } catch (error) {
        return 'Menu Item';
      }
    };
    
    return (
      <Link
        to={path}
        className={`flex items-center text-sm rounded-lg transition-all duration-200 relative group sidebar-item ${
          active 
            ? 'bg-white text-[#0099e0] font-semibold shadow-sm' 
            : 'text-white hover:bg-white/10'
        }`}
        style={{
          padding: '10px 12px',
          textDecoration: 'none',
        }}
        title={isCollapsed ? getTextContent() : ''}
        {...props}
      >
        <div className="flex-shrink-0 w-5 h-5 mr-3">
          {icon}
        </div>
        <span className={`${isCollapsed ? 'hidden' : 'block'}`}>
          {getTextContent()}
        </span>
      </Link>
    );
  };

  const SubNavLink = ({ to, children, ...props }) => {
    // Special route mapping for specific paths
    let mappedPath = to;
    
    // Handle special cases that need specific route structures
    if (to === '/sites') {
      mappedPath = '/dashboard/sites';
    } else if (to === '/categories') {
      mappedPath = '/dashboard/categories';
    } else if (to === '/categories/groups') {
      mappedPath = '/dashboard/categories/groups';
    } else if (to === '/assets/purchase_history') {
      mappedPath = '/dashboard/assets/purchase_history';
    } else if (to === '/esr') {
      mappedPath = '/dashboard/specifications/esr';
    } else if (to === '/admin/gl-codes') {
      mappedPath = '/dashboard/settings/gl-codes';
    } else if (to === '/admin/suppliers') {
      mappedPath = '/dashboard/procurement/suppliers';
    } else if (to === '/procurement') {
      mappedPath = '/dashboard/procurement';
    }
    // For other paths, add /dashboard prefix if it doesn't already have it
    else if (!to.startsWith('/dashboard')) {
      mappedPath = `/dashboard${to}`;
    }
    
    const active = isActive(to);
    const isESR = to === '/esr';
    
    return (
      <Link
        to={mappedPath}
        className={`block px-3 py-2 text-sm rounded-md ${
          active
            ? 'bg-white text-[#0099e0] font-semibold'
            : isESR
            ? 'text-white hover:opacity-90'
            : 'text-white hover:bg-white/10'
        }`}
        style={{ 
          textDecoration: 'none',
          backgroundColor: isESR ? '#2AAA8A'
           : 'transparent'
        }}

        {...props}
      >
        {children}
      </Link>
    );
  };

  const MenuButton = ({ menuName, active, children, icon, tooltip }) => {
    // Extract text content for collapsed state
    const getTextContent = () => {
      if (tooltip) return tooltip;
      
      // Handle direct string children
      if (typeof children === 'string') return children;
      
      try {
        // Try to extract text from children structure
        if (children?.props?.children) {
          const childArray = Array.isArray(children.props.children) ? children.props.children : [children.props.children];
          const textChild = childArray.find(child => typeof child === 'string' || child?.props?.children);
          if (typeof textChild === 'string') return textChild;
          if (textChild?.props?.children) return textChild.props.children;
        }
        
      
      } catch (error) {
        return 'Menu Item';
      }
    };

    return (
      <button
        onClick={() => {
          if (!isOpen) {
            toggleSidebar();
          }
          if (isCollapsed) {
            // When collapsed, expand the sidebar and open the menu
            toggleSidebarCollapse();
            setTimeout(() => {
              toggleMenu(menuName);
            }, 100);
          } else {
            toggleMenu(menuName);
          }
        }}
        className={`w-full flex items-center justify-between text-sm rounded-lg transition-all duration-200 relative group sidebar-item ${
          active 
            ? 'bg-white text-[#0099e0] font-semibold shadow-sm' 
            : 'text-white hover:bg-white/10'
        }`}
        style={{
          padding: '10px 12px',
          border: 'none',
          background: 'transparent',
        }}
        title={isCollapsed ? getTextContent() : ''}
      >
        <div className="flex items-center">
          <div className="flex-shrink-0 w-5 h-5 mr-3">
            {icon}
          </div>
          <span className={`${isCollapsed ? 'hidden' : 'block'}`}>
            {typeof children === 'string' ? children : getTextContent()}
          </span>
        </div>
        {!isCollapsed && (
          <svg
            className={`w-4 h-4 transition-transform duration-200 ${
              openMenus[menuName] ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m19 9-7 7-7-7" />
          </svg>
        )}
      </button>
    );
  };

  return (
    <div 
      className={`${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 fixed inset-y-0 left-0 z-50 transition-transform duration-300 ease-in-out lg:relative lg:inset-0 
        text-white shadow-xl flex flex-col sidebar-transition h-screen ${isCollapsed ? 'sidebar-collapsed' : ''} flex-shrink-0`}
      style={{
        background: SIDEBAR_THEMES[sidebarTheme] || SIDEBAR_THEMES.default,
        width: isCollapsed ? '64px' : '256px',
        minWidth: isCollapsed ? '64px' : '256px',
        maxWidth: isCollapsed ? '64px' : '256px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div className="flex flex-col h-full">
        {/* Enhanced Logo and Controls */}
        <div className={`h-16 flex items-center bg-white shrink-0 ${isCollapsed ? 'justify-center px-2' : 'justify-between px-4'}`}>
          {!isCollapsed ? (
            <>
              <div className="flex items-center">
                {siteLogo ? (
                  <img src={siteLogo} alt="Site Logo" className="h-12" />
                ) : (
                  <span className="text-xl font-bold text-neutral-800">Silulumanzi</span>
                )}
              </div>
              <button onClick={toggleSidebar} className="text-neutral-500 hover:text-neutral-800 lg:hidden" aria-label="Close sidebar">
                <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </>
          ) : (
            <div className="flex flex-col items-center w-full space-y-2">
              <button
                onClick={toggleSidebarCollapse}
                className="w-8 h-8 rounded-lg bg-[#0099e0] flex items-center justify-center hover:bg-[#0088cc] transition-colors cursor-pointer"
                title="Expand Sidebar"
              >
                <span className="text-white font-bold text-sm">S</span>
              </button>
              <button
                onClick={toggleSidebarCollapse}
                className="w-5 h-5 flex items-center justify-center text-neutral-600 hover:text-neutral-800 transition-colors"
                title="Expand Sidebar"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>


        {/* Enhanced Menu Items */}
        <div 
          className={`flex-1 py-4 overflow-y-auto overflow-x-hidden relative z-10`}
          style={{
            padding: isCollapsed ? '16px 8px' : '16px 12px',
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(255, 255, 255, 0.3) transparent',
            zIndex: 10,
            maxHeight: 'calc(100vh - 200px)' // Reserve space for header and weather widget
          }}
        >
          <ul 
            className="space-y-1"
            style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
            }}
          >
            <li>
              <NavLink to="/dashboard" exact icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path></svg>}>
                Dashboard
              </NavLink>
            </li>  

            {/* Procurement */}
            {(can('View RFQ') || can('Create Specification')) && (
              <li className="relative">
                <MenuButton 
                  menuName="procurement" 
                  active={isActive('/dashboard/procurement') || isActive('/specifications') || isActive('/requisitions') || isActive('/rfqs')}
                  icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"></path></svg>}
                  tooltip="Procurement"
                >
                  Procurement
                </MenuButton>
                {openMenus.procurement && !isCollapsed && (
                  <div className="ml-4 mt-2 space-y-1 relative z-30">
                    {(can('Create Specification') || can('Approve Specification') || can('View RFQ')) && (
                      <>
                        <SubNavLink to="/rfqs">RFQ</SubNavLink>
                        <SubNavLink to="/specifications">RFQ Specifications</SubNavLink>
                      </>
                    )}
                    {(can('Create Specification') || can('Approve Requisition')) && (
                      <SubNavLink to="/requisitions?status=all">Requisitions</SubNavLink>
                    )}
                    {(can('Create Specification') || can('Approve Specification') || can('View RFQ')) && (
                      <>
                        <SubNavLink to="/esr">ESR Specifications</SubNavLink>
                        <SubNavLink to="/requisitions/po">Purchase Orders</SubNavLink>
                      </>
                    )}
                    {(isAdmin() || can('Access Procurement Settings') || can('Modify Procurement Settings')) && (
                      <>
                        <SubNavLink to="/procurement/settings">Settings</SubNavLink>
                        <SubNavLink to="/procurement/sla">SLA Management</SubNavLink>
                        <SubNavLink to="/admin/gl-codes">GL Codes</SubNavLink>
                      </>
                    )}
                    {(isProcurement() || isIT() || can('View Suppliers')) && (
                      <>
                        <SubNavLink to="/notices/suppliers">Supplier Notices</SubNavLink>
                        <SubNavLink to="/procurement/supplier_applications">Supplier Applications</SubNavLink>
                      </>
                    )}
                    {can('View Suppliers') && (
                      <>
                        <SubNavLink to="/admin/suppliers">Suppliers</SubNavLink>
                        <SubNavLink to="/procurement/groups">Groups</SubNavLink>
                        <SubNavLink to="/procurement/compliance">COIDA Compliance</SubNavLink>
                      </>
                    )} 
                  </div>
                )}
              </li>
            )}

            {/* Accounting */}
            <li className="relative">
              <MenuButton 
                menuName="accounting" 
                active={isActive('/finance') || isActive('/budgets')}
                icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>}
                tooltip="Accounting"
              >
                Accounting
              </MenuButton>
              {openMenus.accounting && !isCollapsed && (
                <div className="ml-4 mt-2 space-y-1 relative z-30">
                  <SubNavLink to="/budgets">Budget</SubNavLink>
                </div>
              )}
            </li>

            {/* Water Tanker */}
            {(isAdmin() || isIT()) && (
              <li className="relative">
                <MenuButton 
                  menuName="water" 
                  active={isActive('/water') || isActive('/tanker')}
                  icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>}
                  tooltip="Water Tanker"
                >
                  Water Tanker
                </MenuButton>
                {openMenus.water && !isCollapsed && (
                  <div className="ml-4 mt-2 space-y-1 relative z-30">
                    <SubNavLink to="/tanker/dashboard">Dashboard</SubNavLink>
                    <SubNavLink to="/tanker/transactions">Transactions</SubNavLink>
                    <SubNavLink to="/tanker/vehicles">Vehicles</SubNavLink>
                    <SubNavLink to="/tanker/permits">Permits</SubNavLink>
                    <SubNavLink to="/tanker/users">App Users</SubNavLink>
                    <SubNavLink to="/tanker/pricing">Pricing</SubNavLink>
                    <SubNavLink to="/tanker/settings">App Settings</SubNavLink>
                  </div>
                )}
              </li>
            )}

            {/* MMS - Maintenance Management System */}
            {(isIT()) && (
              <li className="relative">
                <MenuButton 
                  menuName="mms" 
                  active={isActive('/mms')}
                  icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path></svg>}
                  tooltip="MMS"
                >
                  MMS
                </MenuButton>
                {openMenus.mms && !isCollapsed && (
                  <div className="ml-4 mt-2 space-y-1 relative z-30">
                    <SubNavLink to="/mms/dashboard">Dashboard</SubNavLink>
                    <SubNavLink to="/mms/jobcards">Job Cards</SubNavLink>
                    <SubNavLink to="/mms/workers">Artisans</SubNavLink>
                    <SubNavLink to="/mms/locations">Live Map</SubNavLink>
                    <SubNavLink to="/mms/calendar">Calendar</SubNavLink>
                    <SubNavLink to="/mms/assets">Assets</SubNavLink>
                    <SubNavLink to="/mms/paused-jobs">Paused Jobs</SubNavLink>
                    <SubNavLink to="/mms/options">Options</SubNavLink>
                  </div>
                )}
              </li>
            )}

            {/* Asset Management */}
            {(can('Manage Assets') || can('View Assets')) && (
              <li className="relative">
                <MenuButton 
                  menuName="assets" 
                  active={isActive('/assets/hardware')}
                  icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>}
                  tooltip="Asset Management"
                >
                  Asset Management
                </MenuButton>
                {openMenus.assets && !isCollapsed && (
                  <div className="ml-4 mt-2 space-y-1 relative z-30">
                    <SubNavLink to="/assets/hardware">Hardware</SubNavLink>
                    <SubNavLink to="/assets/software">Software</SubNavLink>
                    <SubNavLink to="/assets/systems">Systems</SubNavLink>
                    <SubNavLink to="/assets/purchase_history">Purchase History</SubNavLink>
                    <SubNavLink to="/assets/onsite">On-Site</SubNavLink>
                    <SubNavLink to="/assets/bin">Disposed</SubNavLink>
                    {can('Manage Assets') && (
                      <SubNavLink to="/assets/settings">Settings</SubNavLink>
                    )}
                  </div>
                )}
              </li>
            )}

            {/* IT - IMS */}
            <li className="relative">
              <MenuButton 
                menuName="it" 
                active={isActive('/chats') || isActive('/development') || isActive('/helpdesk') || isActive('/documentation')}
                icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 01-2 2z"></path></svg>}
                tooltip="IT - IMS"
              >
                IT - IMS
              </MenuButton>
              {openMenus.it && !isCollapsed && (
                <div className="ml-4 mt-2 space-y-1 relative z-30">
                  {/* isIT() && <SubNavLink to="/chats">Chat</SubNavLink>  */}
                  {/* <SubNavLink to="/help/tickets">Helpdesk (Legacy)</SubNavLink> */}
                  <SubNavLink to="/helpdesk">Helpdesk</SubNavLink>
                  {isIT() && (
                    <> 
                      <SubNavLink to="/helpdesk/ratings">Helpdesk Rating</SubNavLink>
                      <SubNavLink to="/documentation">Documentation</SubNavLink>
                      <SubNavLink to="/calendar">Calendar</SubNavLink>
                    </>
                  )}
                
                </div>
              )}
            </li>

            {/* Systems Admin */}
            {(isAdmin() || isIT()) && (
              <li className="relative">
                <MenuButton 
                  menuName="users" 
                  active={isActive('/users') || isActive('/employees') || isActive('/departments') || isActive('/sites') || isActive('/roles') || isActive('/categories')}
                  icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>}
                  tooltip="Systems Admin"
                >
                  Systems Admin
                </MenuButton>
                {openMenus.users && !isCollapsed && (
                  <div className="ml-4 mt-2 space-y-1 relative z-30">
                    <SubNavLink to="/users">Users</SubNavLink>
                    {isIT() && <SubNavLink to="/employees">Employees</SubNavLink>}
                    <SubNavLink to="/departments">Departments</SubNavLink>
                    <SubNavLink to="/sites">Sites</SubNavLink>
                    <SubNavLink to="/roles">Roles</SubNavLink>
                    <SubNavLink to="/categories">Categories</SubNavLink> 
                    <SubNavLink to="/settings">Settings</SubNavLink>
                  </div>
                )}
              </li>
            )}

            {/* Reporting */}
            {(
              can('Chat Report') || 
              can('Requisitions Report') || 
              can('Helpdesk Report') ||
              can('RFQ Report') || 
              can('MMS Report') ||
              can('Water Tanker Report')) && (
              <li className="relative">
                <MenuButton 
                  menuName="reports" 
                  active={isActive('/reports')}
                  icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>}
                  tooltip="Reports"
                >
                  Reports
                </MenuButton>
                {openMenus.reports && !isCollapsed && (
                  <div className="ml-4 mt-2 space-y-1 relative z-30">
  
                    {can('Chat Report') && ( 
                        <SubNavLink to="/reports/chat">Chat Reports</SubNavLink> 
                    )}
                     
                    {(can('Requisitions Report')) && ( 
                        <SubNavLink to="/reports/requisitions">Requisitions</SubNavLink>   
                    )}   

                    {(can('RFQ Report')) && (
                       <SubNavLink to="/reports/rfq">RFQ Reports</SubNavLink> 
                    )} 

                    {(can('Helpdesk Report')) && ( 
                        <SubNavLink to="/reports/helpdesk">Helpdesk</SubNavLink> 
                    )} 
                     
                    {can('Water Tanker Report') && (
                      <SubNavLink to="/tanker/reports">Water Tanker</SubNavLink>
                    )}

                     {(can('MMS Report')) && ( 
                        <SubNavLink to="/mms/reports">MMS Reports</SubNavLink>
                    )}
                    
                  </div>
                )}
              </li>
            )}
 
           {/* CRM */}
            <li className="relative">
              <MenuButton 
                menuName="call_center" 
                active={isActive('/posts') || isActive('/notices') || isActive('/areas') || isActive('/chats') || isActive('/customers')  || isActive('/help')}
                icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>}
                tooltip="CRM"
              >
                CRM
              </MenuButton>
              {openMenus.call_center && !isCollapsed && (
                <div className="ml-4 mt-2 space-y-1 relative z-30">
                  {can('Create Post') && <SubNavLink to="/posts">Posts</SubNavLink>}
                  {can('View Notice') && <SubNavLink to="/notices">Notices</SubNavLink>}
                  {can('Update Area') && <SubNavLink to="/areas">Areas</SubNavLink>}
                  {can('View Chat') && <SubNavLink to="/chats">Chat</SubNavLink>}
                  {isCallCenter() && <SubNavLink to="/customers">Customer Details</SubNavLink>}
                  {isCallCenter() && <SubNavLink to="/customers/rejected">Rejected Details</SubNavLink>}
                  {(isCallCenter() || isIT()) && <SubNavLink to="/help_pages">Help Pages</SubNavLink>}
                </div>
              )}
            </li>
     
          </ul>
        </div>

        {/* Professional Weather Widget - Bottom of Sidebar */}
        {!Object.values(openMenus).some(isOpen => isOpen) && !isCollapsed && (
        <div className="border-t border-white/20 relative z-0 overflow-hidden">
          {/* Weather background image */}
          {weatherData && !weatherLoading && !weatherError && (
            <div className="absolute inset-0 opacity-10">
              <img 
                src={`https:${weatherData.icon}`} 
                alt={weatherData.text}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
          {weatherLoading ? (
            <div className={`flex items-center justify-center ${isCollapsed ? 'py-3' : 'py-4'}`}>
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/30 border-t-white"></div>
              {!isCollapsed && <span className="ml-2 text-white/70 text-sm font-medium"></span>}
            </div>
          ) : weatherError || !weatherData ? (
            <div className={`flex items-center justify-center ${isCollapsed ? 'py-3' : 'py-4'}`}>
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                <svg className="w-4 h-4 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              {!isCollapsed && <span className="ml-2 text-white/60 text-sm"></span>}
            </div>
          ) : !isCollapsed ? (
            <div className="p-4 relative z-10">
              {/* Header with location and refresh */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-white/70 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-white/70 text-sm font-medium">Mbombela</span>
                </div>
                <button 
                  onClick={fetchWeather}
                  className="p-1 rounded-md hover:bg-white/10 transition-colors"
                  title="Refresh weather"
                >
                  <svg className="w-4 h-4 text-white/70 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>

              {/* Main weather display */}
              <div className="flex items-center mb-3">
                <div className="ml-3">
                  <div className="text-2xl font-bold text-white">{weatherData.temp}°</div>
                  <div className="text-white/80 text-sm font-medium">{weatherData.text}</div>
                </div>
              </div>

              {/* Weather details grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/10 rounded-lg p-2 border-t border-white/20">
                  <div className="flex items-center mb-1">
                    <svg className="w-3 h-3 text-white/70 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m0 0V1a1 1 0 011-1h2a1 1 0 011 1v18a1 1 0 01-1 1H4a1 1 0 01-1-1V1a1 1 0 011-1h2a1 1 0 011 1v3m0 0h8" />
                    </svg>
                    <span className="text-white/70 text-xs">Wind</span>
                  </div>
                  <div className="text-white text-sm font-semibold">{weatherData.wind} km/h</div>
                </div>
                <div className="bg-white/10 rounded-lg p-2 border-t border-white/20">
                  <div className="flex items-center mb-1">
                    <svg className="w-3 h-3 text-white/70 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                    </svg>
                    <span className="text-white/70 text-xs">Rain</span>
                  </div>
                  <div className="text-white text-sm font-semibold">{weatherData.rain}%</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center py-3 relative z-10">
              <div className="text-white font-bold text-lg">{weatherData.temp}°</div>
              <div className="text-white/80 text-xs text-center">{weatherData.text}</div>
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}

export default Sidebar;